// ---------------------------------------------------------------------------
// GENERATED MIRROR — DO NOT EDIT DIRECTLY.
//
// Source of truth: Zella-Screenings-backend/services/bgvFieldValidators.js
// Copied verbatim (with TypeScript annotations added) because the admin panel
// and backend are separate packages and CRA's rootDir forbids importing across
// the boundary.
//
// After changing the backend module, re-sync with:
//   node Zella-Screenings-backend/scripts/syncBgvValidators.js
// The sync script fails CI if the two files have drifted.
// ---------------------------------------------------------------------------

// Shared validation rules for candidate-submitted BGV form data.
//
// This module is the SINGLE SOURCE OF TRUTH for BGV field rules. It is mirrored
// by zella-admin-panel/src/lib/bgvValidators.ts for inline form feedback; the
// backend re-runs these rules on submit so the public endpoint cannot be
// bypassed by a direct API call.
//
// Deliberately dependency-free and side-effect-free so both tiers can use it.
//
// The Aadhaar/PAN/mobile predicates mirror services/scannerValidators.js, which
// has been in production use on the OCR pipeline.

// ---------------------------------------------------------------------------
// Verhoeff checksum (standard public tables — used for Aadhaar validation)
// ---------------------------------------------------------------------------
const VERHOEFF_D = [
  [0, 1, 2, 3, 4, 5, 6, 7, 8, 9],
  [1, 2, 3, 4, 0, 6, 7, 8, 9, 5],
  [2, 3, 4, 0, 1, 7, 8, 9, 5, 6],
  [3, 4, 0, 1, 2, 8, 9, 5, 6, 7],
  [4, 0, 1, 2, 3, 9, 5, 6, 7, 8],
  [5, 9, 8, 7, 6, 0, 4, 3, 2, 1],
  [6, 5, 9, 8, 7, 1, 0, 4, 3, 2],
  [7, 6, 5, 9, 8, 2, 1, 0, 4, 3],
  [8, 7, 6, 5, 9, 3, 2, 1, 0, 4],
  [9, 8, 7, 6, 5, 4, 3, 2, 1, 0]
];
const VERHOEFF_P = [
  [0, 1, 2, 3, 4, 5, 6, 7, 8, 9],
  [1, 5, 7, 6, 2, 8, 3, 0, 9, 4],
  [5, 8, 0, 3, 7, 9, 6, 1, 4, 2],
  [8, 9, 1, 6, 0, 4, 3, 5, 2, 7],
  [9, 4, 5, 3, 1, 2, 6, 8, 7, 0],
  [4, 2, 8, 6, 5, 7, 3, 9, 0, 1],
  [2, 7, 9, 3, 8, 0, 6, 4, 1, 5],
  [7, 0, 4, 6, 9, 1, 3, 2, 5, 8]
];

export function verhoeffCheck(str: string): boolean {
  if (typeof str !== 'string' || !/^\d+$/.test(str)) return false;
  let c = 0;
  const reversed = str.split('').reverse();
  for (let i = 0; i < reversed.length; i++) {
    c = VERHOEFF_D[c][VERHOEFF_P[i % 8][parseInt(reversed[i], 10)]];
  }
  return c === 0;
}

const str = (v: unknown): string => (typeof v === 'string' ? v.trim() : '');

// ---------------------------------------------------------------------------
// Numeric / format rules
// ---------------------------------------------------------------------------

/**
 * Indian mobile: exactly 10 digits, leading 6-9.
 *
 * Separators are ignored and a leading +91 / 0 trunk prefix is tolerated —
 * candidates routinely paste "+91 98765 43210".
 */
export function isValidMobile(value: unknown): boolean {
  let digits = str(value).replace(/\D/g, '');
  if (digits.length === 12 && digits.startsWith('91')) digits = digits.slice(2);
  else if (digits.length === 11 && digits.startsWith('0')) digits = digits.slice(1);
  return /^[6-9]\d{9}$/.test(digits);
}

/**
 * Aadhaar: exactly 12 digits.
 *
 * The Verhoeff checksum is deliberately NOT enforced here. It was tried and
 * reverted: it blocked live candidates whose real Aadhaar had a single
 * mistyped digit, with an error message that gave them no way to understand
 * why a 12-digit number was rejected. Length is the rule that stops the
 * gibberish this validation exists to catch.
 *
 * verhoeffCheck remains exported for the OCR pipeline, which uses it to flag
 * low-confidence extractions for review rather than to block a submission.
 */
export function isValidAadhaar(value: unknown): boolean {
  const digits = str(value).replace(/\D/g, '');
  if (!/^\d{12}$/.test(digits)) return false;
  // Reject all-same-digit placeholders ("111111111111"), which are dummy input
  // rather than a mistyped real number.
  if (/^(\d)\1{11}$/.test(digits)) return false;
  return true;
}

/** PAN: 5 letters + 4 digits + 1 letter. Case-insensitive. */
export function isValidPan(value: unknown): boolean {
  return /^[A-Z]{5}\d{4}[A-Z]$/.test(str(value).toUpperCase());
}

/** Year of passing: 4-digit year between 1950 and (current year + 6). */
export function isValidYear(value: unknown): boolean {
  const v = str(value);
  if (!/^\d{4}$/.test(v)) return false;
  const year = parseInt(v, 10);
  return year >= 1950 && year <= new Date().getFullYear() + 6;
}

/** CTC: numeric. Currency symbols, commas and spaces are stripped first. */
export function isValidCtc(value: unknown): boolean {
  const cleaned = str(value).replace(/[₹$,\s]/g, '');
  if (cleaned === '') return false;
  return /^\d+(\.\d+)?$/.test(cleaned);
}

/** Standard email shape: name@company.tld */
export function isValidEmail(value: unknown): boolean {
  const v = str(value);
  if (!v || /\s/.test(v)) return false;
  return /^[^\s@]+@[^\s@]+\.[A-Za-z]{2,}$/.test(v);
}

/** True when `later` is strictly after `earlier`. Blank values pass (handled by required-checks). */
export function isAfter(later: unknown, earlier: unknown): boolean {
  const a = str(later);
  const b = str(earlier);
  if (!a || !b) return true;
  const da = new Date(a);
  const db = new Date(b);
  if (isNaN(da.getTime()) || isNaN(db.getTime())) return true;
  return da.getTime() > db.getTime();
}

/** Normalisers applied before persisting. */
export const normalise = {
  // Mirrors isValidMobile's prefix handling so the stored value is the bare
  // 10-digit number that was actually validated.
  mobile: (v: unknown): string => {
    let digits = str(v).replace(/\D/g, '');
    if (digits.length === 12 && digits.startsWith('91')) digits = digits.slice(2);
    else if (digits.length === 11 && digits.startsWith('0')) digits = digits.slice(1);
    return digits;
  },
  aadhaar: (v: unknown): string => str(v).replace(/\D/g, ''),
  pan: (v: unknown): string => str(v).toUpperCase(),
  ctc: (v: unknown): string => str(v).replace(/[₹$,\s]/g, '')
};

// ---------------------------------------------------------------------------
// Gibberish detection
// ---------------------------------------------------------------------------
// Structural heuristics only — no dictionary. Tuned so that real Indian names,
// universities and qualifications pass while keyboard-mashing fails. The corpus
// in scripts/bgvValidatorCorpus.js is the regression guard for this tuning.

// 'y' counts as a vowel: "Symbiosis", "Mysuru", "Krishnamurthy" depend on it.
const VOWELS = 'aeiouy';

// Sequences that indicate a finger dragged across the keyboard — rows in both
// directions, plus the vertical/diagonal columns ("qaz", "wsx", "edc") that a
// row-only check misses.
const KEYBOARD_ROWS = [
  'qwertyuiop', 'asdfghjkl', 'zxcvbnm',
  '1234567890', 'poiuytrewq', 'lkjhgfdsa', 'mnbvcxz'
];

// Only full 3-letter column walks. Two-letter fragments like "ol"/"lo" occur
// constantly in real words ("Technology"), so they are excluded entirely.
const KEYBOARD_COLUMNS = [
  'qaz', 'wsx', 'edc', 'rfv', 'tgb', 'yhn', 'ujm', 'plm',
  'zaq', 'xsw', 'cde', 'vfr', 'bgt', 'nhy', 'mju', 'mlp'
];

/** 4+ characters appearing consecutively on one keyboard row. */
function hasKeyboardRun(lower: string): boolean {
  const letters = lower.replace(/[^a-z0-9]/g, '');
  for (const row of KEYBOARD_ROWS) {
    for (let i = 0; i + 4 <= row.length; i++) {
      if (letters.includes(row.slice(i, i + 4))) return true;
    }
  }
  // Two adjacent column-walks back to back ("qaz"+"wsx") is mashing; a single
  // 3-letter column can occur inside real words, so one alone does not count.
  let columnHits = 0;
  for (const col of KEYBOARD_COLUMNS) {
    if (letters.includes(col)) columnHits++;
  }
  return columnHits >= 2;
}

/**
 * The WHOLE token is a short unit repeated back to back: "asdasd", "jkjkjk".
 *
 * Anchored to the entire token rather than scanning for any internal repeat —
 * an internal scan false-fires on ordinary words ("competitive" contains "eti"
 * twice) and on enrollment codes containing years ("ENR2020123").
 */
function isWhollyRepeatedUnit(token: string): boolean {
  const letters = token.replace(/[^a-z0-9]/g, '');
  // Requires 6+ chars: real words like "tata", "mama" and "dada" are exactly a
  // 2-char unit doubled, so a 4-char threshold rejects legitimate names.
  if (letters.length < 6) return false;
  for (let unit = 1; unit <= Math.floor(letters.length / 2); unit++) {
    if (letters.length % unit !== 0) continue;
    const head = letters.slice(0, unit);
    const parts = letters.match(new RegExp(`.{1,${unit}}`, 'g')) || [];
    if (parts.every((p: string) => p === head)) return true;
  }
  return false;
}

/**
 * A token is implausible when it has a very long consonant run.
 *
 * With 'y' treated as a vowel, genuine Indian proper nouns top out around four
 * consecutive consonants ("Krishnamurthy" -> shnm, "Chhattisgarh" -> chh/tt sg).
 * Five or more is reliably mashing. A whitelist of "legal clusters" was tried
 * first and abandoned: real names produce far too many valid clusters to
 * enumerate, and every miss blocks a real candidate.
 */
function hasImplausibleConsonantRun(word: string): boolean {
  const letters = word.replace(/[^a-z]/g, '');
  if (letters.length < 5) return false;
  // Threshold is 6, not 5: "strengths" (ngths) and "schwartz" (schw) are real
  // words with 4-5 consonant runs. Anything at 6+ is mashing.
  return letters
    .split(new RegExp(`[${VOWELS}]+`))
    .filter(Boolean)
    .some((run) => run.length >= 6);
}

/**
 * Vowel-to-letter ratio sanity check.
 *
 * The floor is deliberately low. English packs consonants far denser than it
 * first appears — "growth" (2/6), "strength" (1/8), "knight" and "bright"
 * (2/6 counting y) are all ordinary words, and a 0.2 floor rejected every one
 * of them, blocking "Career Growth" as a reason for leaving. Only a word with
 * essentially no vowels at all is treated as mashing here; the keyboard-run
 * and repeated-unit rules do the real work.
 */
function hasImplausibleVowelRatio(word: string): boolean {
  const letters = foldConsonantalY(word.replace(/[^a-z]/g, ''));
  if (letters.length < 5) return false;
  const vowels = letters.split('').filter((c: string) => VOWELS.includes(c)).length;
  const ratio = vowels / letters.length;
  return ratio < 0.12 || ratio > 0.85;
}

/**
 * In "ayya", "iyya", "ayyub" the doubled y is a consonant, not a vowel. This is
 * pervasive in transliterated Indian names — Miyya, Ayyappan, Krishnayya,
 * Ramayya, Subbayya, Sayyad, Ayyub — and counting it as a vowel made every one
 * of them look like a four-vowel run. Collapse "yy" to a consonant marker
 * before the run and ratio checks so those names read normally.
 *
 * A single y between two different vowels is a glide consonant too —
 * Vaiyapuri, Kaiyum, Saiyed, Raiyan, Bhuiyan — and "aiya" was being rejected
 * as a four-vowel run, blocking a live candidate's father's name. A y between
 * the SAME vowel ("eeye" in "rheeye5") is left alone so mashing is still caught.
 */
function foldConsonantalY(letters: string): string {
  return letters
    .replace(/yy/g, 'j')
    .replace(/([aeiou])y(?=[aeiou])/g, (m, before, offset, all) =>
      before === all[offset + 2] ? m : `${before}j`);
}

/**
 * Four or more consecutive vowels ("rheeye5" -> "eeye").
 *
 * This is the signal that catches mashing which happens to contain vowels and
 * therefore slips past every other rule. English and transliterated Indian
 * words effectively never stack four vowels in a row; the longest common runs
 * are three ("beau", "aayi").
 */
function hasImplausibleVowelRun(word: string): boolean {
  const letters = foldConsonantalY(word.replace(/[^a-z]/g, ''));
  return new RegExp(`[${VOWELS}]{4,}`).test(letters);
}

/**
 * Two-letter values that are real qualifications or titles. Anything else of
 * length 2 is treated as placeholder input.
 */
const SHORT_QUALIFICATIONS = new Set([
  'be', 'me', 'ba', 'bs', 'ma', 'ms', 'ca', 'cs', 'cf', 'cm', 'md', 'do',
  'jd', 'ed', 'bd', 'dm', 'ch', 'pg', 'ug', 'hr', 'it', 'qa', 'sr', 'jr'
]);

/** Words allowed to bypass the vowel requirement (common abbreviations). */
const KNOWN_ABBREVIATIONS = new Set([
  'bsc', 'msc', 'mba', 'bca', 'mca', 'bba', 'phd', 'llb', 'llm', 'bds', 'mds',
  'mbbs', 'btech', 'mtech', 'be', 'me', 'ba', 'ma', 'bcom', 'mcom', 'iit',
  'nit', 'bits', 'vtu', 'jntu', 'ignou', 'cbse', 'icse', 'hsc', 'ssc', 'pg',
  'ug', 'diploma', 'hons', 'st', 'dr', 'mr', 'ms', 'jr', 'sr', 'ltd', 'pvt',
  'inc', 'llp', 'hr', 'it', 'ceo', 'cto', 'vp', 'sde', 'qa'
]);

/**
 * Reject placeholder / keyboard-mash text while accepting real-world values.
 *
 * @param {string} value
 * @param {{ minLength?: number, allowCode?: boolean }} [opts]
 *   minLength — minimum trimmed length (default 2; "BE", "ME", "CA" and "BA"
 *               are real degrees, so 3 wrongly rejected them)
 *   allowCode — treat as an identifier (enrollment no.); skips the vowel,
 *               consonant-run and vowel-ratio rules, which do not apply to
 *               alphanumeric codes like "1RV18CS045".
 */
export function isMeaningfulText(value: unknown, opts: { minLength?: number; allowCode?: boolean } = {}): boolean {
  const { minLength = 2, allowCode = false } = opts;
  const v = str(value);

  if (v.length < minLength) return false;

  // Two-character values are accepted only when they are known qualifications
  // ("BE", "ME", "CA"). Without this, lowering minLength to admit real degrees
  // would also admit "ab", "xx" and "df".
  if (v.replace(/[^A-Za-z0-9]/g, '').length === 2) {
    return SHORT_QUALIFICATIONS.has(v.toLowerCase().replace(/[^a-z]/g, ''));
  }

  const lower = v.toLowerCase();

  // 3+ identical LETTERS in a row: "xxxx", "aaaa". Digits are exempt —
  // PIN codes and house numbers legitimately repeat ("560001", "700016").
  if (/([a-z])\1{2,}/.test(lower)) return false;

  const tokens = lower.split(/[^a-z0-9']+/).filter(Boolean);

  if (allowCode) {
    // Identifiers are alphanumeric: "1RV18CS045" and "20BCE1234" mix letters
    // and digits, but plenty of universities issue all-digit roll numbers
    // ("202112345678"), so a letter is NOT required here — only that the value
    // is made of digits/letters and is not a repeated placeholder ("000000").
    if (!/[a-z0-9]/.test(lower)) return false;
    if (tokens.some((t: string) => isWhollyRepeatedUnit(t))) return false;
    return v.length >= Math.max(minLength, 4);
  }

  // Must contain at least one letter — "1234" and "...." are not names.
  // Identifiers are exempt and returned above.
  if (!/[a-z]/.test(lower)) return false;

  if (hasKeyboardRun(lower)) return false;
  if (tokens.some((t: string) => isWhollyRepeatedUnit(t))) return false;

  // Placeholder words people type to get past a form.
  if (/^(test|testing|dummy|sample|abc|xyz|asdf|qwerty|na|n\/a|none|nil|null|undefined|foo|bar|temp|blah)$/i.test(lower)) {
    return false;
  }

  if (tokens.length === 0) return false;

  for (const word of tokens) {
    const letters = word.replace(/[^a-z]/g, '');
    // Purely numeric tokens (PIN codes, house numbers, years) carry no phonetic
    // structure — "560001" is a valid part of an address, not gibberish.
    if (letters.length === 0) continue;

    // A vowel run is implausible at any length: "eeye" in "rheeye5".
    if (hasImplausibleVowelRun(word)) return false;

    // Short tokens and known abbreviations bypass the remaining phonetic checks.
    if (letters.length <= 3 || KNOWN_ABBREVIATIONS.has(letters)) continue;

    if (!letters.split('').some((c: string) => VOWELS.includes(c))) return false;
    if (hasImplausibleConsonantRun(word)) return false;
    if (hasImplausibleVowelRatio(word)) return false;
  }

  return true;
}

// ---------------------------------------------------------------------------
// Whole-form validation
// ---------------------------------------------------------------------------

/**
 * Date the newer mandatory fields came into force — education period of study
 * and course type, and the employment period/CTC/employee-ID/HR/reason fields.
 * Collections created on or after this date must supply them; earlier records
 * are grandfathered so in-flight candidates are never blocked on fields that
 * were optional when they filled the form.
 */
export const EDUCATION_PERIOD_REQUIRED_FROM = new Date('2026-08-26T00:00:00Z');

/** True when a collection created at `createdAt` must supply the new fields. */
export function requiresEducationPeriod(createdAt?: string | Date | null): boolean {
  if (!createdAt) return false;
  const created = new Date(createdAt);
  if (Number.isNaN(created.getTime())) return false;
  return created >= EDUCATION_PERIOD_REQUIRED_FROM;
}

export const MESSAGES = {
  mobile: 'Enter a valid 10-digit mobile number',
  aadhaar: 'Enter a valid 12-digit Aadhaar number',
  pan: 'Enter a valid PAN (e.g. ABCDE1234F)',
  year: 'Enter a valid 4-digit year',
  ctc: 'Enter numbers only',
  email: 'Enter a valid email (e.g. name@company.com)',
  text: 'Enter a valid value',
  // A digits-only value in a descriptive field is almost always a phone number
  // or ID pasted into the wrong box. Saying so beats "Enter a valid value",
  // which leaves the candidate retyping the same thing.
  numericInTextField: 'This looks like a number — please enter text here',
  reason: 'Enter a meaningful reason (at least 10 characters)',
  required: 'This field is required',
  periodOrder: 'Period To must be after Period From',
  addressType: 'Select at least one current and one permanent address',
  addressDuration: 'Enter the duration of stay'
};

const has = (v: unknown): boolean => str(v).length > 0;

/**
 * Message for a rejected free-text field. Distinguishes "you put a number in a
 * text box" from generic gibberish, because the fix is different and the
 * candidate cannot guess it from "Enter a valid value".
 */
const textError = (value: unknown): string =>
  /^[\d\s+()-]+$/.test(str(value)) ? MESSAGES.numericInTextField : MESSAGES.text;

/**
 * Validate candidate-submitted BGV form data.
 *
 * Only non-empty values are format-checked, except where a field is explicitly
 * required — a candidate can leave optional fields blank, but must not fill
 * them with gibberish.
 *
 * @param {object} formData
 * @param {{ steps?: object }} [config] - enabled steps from the collection's formConfig
 * @returns {Record<string, string>} field path -> message (empty when valid)
 */
export function validateBGVFormData(formData: any = {}, config: any = {}): Record<string, string> {
  const errors: Record<string, string> = {};
  const steps = config.steps || {};
  const stepOn = (key: string) => steps[key] !== false;

  const set = (path: string, msg: string) => {
    if (!errors[path]) errors[path] = msg;
  };

  // --- Personal info -------------------------------------------------------
  const pi = formData.personalInfo || {};

  if (!has(pi.fullName)) set('personalInfo.fullName', MESSAGES.required);
  else if (!isMeaningfulText(pi.fullName)) set('personalInfo.fullName', textError(pi.fullName));

  if (has(pi.fathersName) && !isMeaningfulText(pi.fathersName)) {
    set('personalInfo.fathersName', textError(pi.fathersName));
  }

  if (!has(pi.mobile)) set('personalInfo.mobile', MESSAGES.required);
  else if (!isValidMobile(pi.mobile)) set('personalInfo.mobile', MESSAGES.mobile);

  if (has(pi.alternateNumber) && !isValidMobile(pi.alternateNumber)) {
    set('personalInfo.alternateNumber', MESSAGES.mobile);
  }

  if (!has(pi.email)) set('personalInfo.email', MESSAGES.required);
  else if (!isValidEmail(pi.email)) set('personalInfo.email', MESSAGES.email);

  if (!has(pi.aadhaarNumber)) set('personalInfo.aadhaarNumber', MESSAGES.required);
  else if (!isValidAadhaar(pi.aadhaarNumber)) set('personalInfo.aadhaarNumber', MESSAGES.aadhaar);

  if (!has(pi.panNumber)) set('personalInfo.panNumber', MESSAGES.required);
  else if (!isValidPan(pi.panNumber)) set('personalInfo.panNumber', MESSAGES.pan);

  // --- Addresses -----------------------------------------------------------
  const addresses = Array.isArray(pi.addresses) ? pi.addresses : [];
  addresses.forEach((addr: any, i: number) => {
    // A wholly blank row is skipped, exactly as employment and reference rows
    // are. The form ships one empty address by default, so requiring it would
    // reject every candidate who never typed an address.
    const addrFilled = has(addr.address) || has(addr.duration) ||
      addr.durationYears != null || addr.durationMonths != null || has(addr.addressType);
    if (!addrFilled) return;

    if (has(addr.address) && !isMeaningfulText(addr.address, { minLength: 10 })) {
      set(`personalInfo.addresses.${i}.address`, textError(addr.address));
    }

    // Duration is NOT required. Candidates who began the form before the
    // structured duration fields existed have neither `durationYears` nor the
    // old free-text `duration`, and blocking them strands an in-flight
    // submission on a field that did not exist when they started. Only the
    // value's shape is checked, when a value is actually present.
    const years = addr.durationYears;
    const months = addr.durationMonths;
    if (years != null && (Number.isNaN(Number(years)) || Number(years) < 0)) {
      set(`personalInfo.addresses.${i}.durationYears`, MESSAGES.addressDuration);
    }
    if (months != null && (Number.isNaN(Number(months)) || Number(months) < 0 || Number(months) > 11)) {
      set(`personalInfo.addresses.${i}.durationMonths`, MESSAGES.addressDuration);
    }
  });

  // Address type is likewise not required — it is new, optional metadata that
  // feeds later routing work. Requiring a current/permanent pair would block
  // every candidate mid-form and every legacy record.

  // --- Education -----------------------------------------------------------
  if (stepOn('education')) {
    const ed = formData.education || {};

    if (!has(ed.degree)) set('education.degree', MESSAGES.required);
    else if (!isMeaningfulText(ed.degree)) set('education.degree', textError(ed.degree));

    if (has(ed.enrollmentNo) && !isMeaningfulText(ed.enrollmentNo, { allowCode: true })) {
      set('education.enrollmentNo', textError(ed.enrollmentNo));
    }

    if (!has(ed.yearOfPassing)) set('education.yearOfPassing', MESSAGES.required);
    else if (!isValidYear(ed.yearOfPassing)) set('education.yearOfPassing', MESSAGES.year);

    if (!has(ed.universityName)) set('education.universityName', MESSAGES.required);
    else if (!isMeaningfulText(ed.universityName)) set('education.universityName', textError(ed.universityName));

    if (has(ed.universityLocation) && !isMeaningfulText(ed.universityLocation)) {
      set('education.universityLocation', textError(ed.universityLocation));
    }

    // Period of study and course type are required, but only for records that
    // began after the fields became mandatory. A candidate who already saved
    // Education without them — and every legacy record — would otherwise be
    // stranded on a step they had already completed. `enforceEducationPeriod`
    // is set by the caller from the collection's creation date.
    if (config.enforceEducationPeriod) {
      if (!has(ed.periodOfStudyFrom)) set('education.periodOfStudyFrom', MESSAGES.required);
      if (!has(ed.periodOfStudyTo)) set('education.periodOfStudyTo', MESSAGES.required);
      if (!has(ed.courseType)) set('education.courseType', MESSAGES.required);
    }

    // The ordering check applies to everyone: whenever both dates are present,
    // they must be in order, regardless of when the record was created.
    if (!isAfter(ed.periodOfStudyTo, ed.periodOfStudyFrom)) {
      set('education.periodOfStudyTo', MESSAGES.periodOrder);
    }
  }

  // --- Employment ----------------------------------------------------------
  if (stepOn('employment')) {
    const employments = Array.isArray(formData.employmentHistory) ? formData.employmentHistory : [];
    employments.forEach((emp: any, i: number) => {
      const p = (f: string) => `employmentHistory.${i}.${f}`;
      // Blank rows are skipped: candidates may have fewer than the max entries.
      const filled = Object.values(emp || {}).some((val: unknown) => has(val));
      if (!filled) return;

      // Fields required on a filled employment row. The Supervisor block is
      // deliberately absent: candidates often cannot reach a former supervisor,
      // so those four stay optional and are only format-checked when supplied.
      if (config.enforceEmploymentDetails) {
        if (!has(emp.periodFrom)) set(p('periodFrom'), MESSAGES.required);
        if (!has(emp.periodTo)) set(p('periodTo'), MESSAGES.required);
        if (!has(emp.ctc)) set(p('ctc'), MESSAGES.required);
        if (!has(emp.employeeId)) set(p('employeeId'), MESSAGES.required);
      }

      // The HR block and Reason for Leaving are NOT required.
      //
      // They were made mandatory on 2026-08-26 and caused sustained candidate
      // escalations: a candidate whose previous employer has shut down, or who
      // simply has no HR contact, cannot supply them and has no way past the
      // step. The workaround people reached for -- typing "Not Applicable" into
      // HR Email and "0000000000" into HR Contact -- then hit the format rules
      // below, so the form rejected both the honest answer and the workaround.
      //
      // Leaving them blank is now accepted. hrName and reasonForLeaving take
      // free text as-is (including "Not Applicable"), because a wrong-looking
      // name costs the verification team nothing. hrContact and hrEmail keep
      // their format checks when a value IS supplied: those two are dialled and
      // emailed by the verification team, and "0000000000" in a report is worse
      // than an empty box -- the candidate should leave them blank instead.

      if (has(emp.companyName) && !isMeaningfulText(emp.companyName)) set(p('companyName'), textError(emp.companyName));
      if (has(emp.designation) && !isMeaningfulText(emp.designation)) set(p('designation'), textError(emp.designation));
      if (has(emp.ctc) && !isValidCtc(emp.ctc)) set(p('ctc'), MESSAGES.ctc);
      if (has(emp.employeeId) && !isMeaningfulText(emp.employeeId, { allowCode: true })) {
        set(p('employeeId'), textError(emp.employeeId));
      }

      if (has(emp.supervisorName) && !isMeaningfulText(emp.supervisorName)) set(p('supervisorName'), textError(emp.supervisorName));
      if (has(emp.supervisorContact) && !isValidMobile(emp.supervisorContact)) set(p('supervisorContact'), MESSAGES.mobile);
      if (has(emp.supervisorEmail) && !isValidEmail(emp.supervisorEmail)) set(p('supervisorEmail'), MESSAGES.email);

      // The entire HR block is stored verbatim with NO format or quality check:
      // hrName, hrContact, hrEmail and reasonForLeaving.
      //
      // Format checks on hrContact/hrEmail were dropped deliberately (product
      // decision, Hexaware escalations). Candidates whose employer has shut
      // down answer "Not Applicable" / "0000000000", and rejecting that is what
      // generated the escalations -- the check blocked the honest answer and
      // the workaround alike, with no third option the candidate could give.
      //
      // Consequence for downstream consumers: these four fields are FREE TEXT
      // and may contain placeholders. Do not assume hrContact is dialable or
      // hrEmail is deliverable -- verification tooling and report generation
      // must treat a non-conforming value as "not supplied" rather than
      // attempting contact. Supervisor contact/email keep their format checks
      // and remain the reliable channel.

      if (!isAfter(emp.periodTo, emp.periodFrom)) set(p('periodTo'), MESSAGES.periodOrder);
    });
  }

  // --- References ----------------------------------------------------------
  if (stepOn('references')) {
    const refs = Array.isArray(formData.references) ? formData.references : [];
    refs.forEach((ref: any, i: number) => {
      const p = (f: string) => `references.${i}.${f}`;
      const filled = Object.values(ref || {}).some((val: unknown) => has(val));
      if (!filled) return;

      if (has(ref.name) && !isMeaningfulText(ref.name)) set(p('name'), textError(ref.name));
      if (has(ref.designation) && !isMeaningfulText(ref.designation)) set(p('designation'), textError(ref.designation));
      if (has(ref.organization) && !isMeaningfulText(ref.organization)) set(p('organization'), textError(ref.organization));
      if (has(ref.relationship) && !isMeaningfulText(ref.relationship)) set(p('relationship'), textError(ref.relationship));
      if (has(ref.contact) && !isValidMobile(ref.contact)) set(p('contact'), MESSAGES.mobile);
      if (has(ref.email) && !isValidEmail(ref.email)) set(p('email'), MESSAGES.email);
    });
  }

  // --- Gap details ---------------------------------------------------------
  if (stepOn('gapDetails')) {
    const gaps = Array.isArray(formData.gapDetails) ? formData.gapDetails : [];
    gaps.forEach((gap: any, i: number) => {
      if (gap?.hasGap !== 'yes') return;
      // Answering "Yes" makes duration and reason mandatory and quality-checked.
      if (!has(gap.duration)) set(`gapDetails.${i}.duration`, MESSAGES.required);
      else if (!isMeaningfulText(gap.duration, { allowCode: true })) {
        set(`gapDetails.${i}.duration`, textError(gap.duration));
      }

      if (!has(gap.reason)) set(`gapDetails.${i}.reason`, MESSAGES.required);
      else if (!isMeaningfulText(gap.reason, { minLength: 10 })) {
        set(`gapDetails.${i}.reason`, MESSAGES.reason);
      }
    });
  }

  return errors;
}

const bgvFieldValidators = {
  verhoeffCheck,
  isValidMobile,
  isValidAadhaar,
  isValidPan,
  isValidYear,
  isValidCtc,
  isValidEmail,
  isAfter,
  isMeaningfulText,
  validateBGVFormData,
  requiresEducationPeriod,
  EDUCATION_PERIOD_REQUIRED_FROM,
  MESSAGES,
  normalise
};

export default bgvFieldValidators;
